/**
 * `localStorage` / `sessionStorage` that keep working when the browser blocks
 * them.
 *
 * Strict privacy settings (Safari's "Block All Cookies", Chrome blocking site
 * data), some in-app browsers and sandboxed frames throw a SecurityError on
 * every access, even on reading `window.localStorage` itself. Each storage
 * here falls back to memory then: values last for the current page load only,
 * which is enough between the funnel's steps (client-side navigation). Values
 * needed after a full page load (the return from Stripe, an email link) must
 * not rely on storage at all: they travel in the URL or come from the Supabase
 * session or the server.
 *
 * NOTE: Use these instead of `window.localStorage` / `window.sessionStorage`
 * everywhere.
 */
interface SafeStorage {
  getItem(key: string): string | null
  removeItem(key: string): void
  /**
   * Returns whether the browser kept the value (false: kept in memory for
   * this page load only).
   */
  setItem(key: string, value: string): boolean
}

export function createSafeStorage(getStorage: () => Storage): SafeStorage {
  // NOTE: Only holds what the browser refused, so a value written while
  // storage works is never shadowed by an older one kept here.
  const memory = new Map<string, string>()

  return {
    getItem(key) {
      if (memory.has(key)) return memory.get(key)!

      try {
        return getStorage().getItem(key)
      } catch {
        return null
      }
    },
    removeItem(key) {
      memory.delete(key)

      try {
        getStorage().removeItem(key)
      } catch {
        // NOTE: Storage blocked: nothing stored there either.
      }
    },
    setItem(key, value) {
      try {
        getStorage().setItem(key, value)
        memory.delete(key)

        return true
      } catch {
        // NOTE: Blocked, or full (QuotaExceededError).
        memory.set(key, value)

        return false
      }
    },
  }
}

// NOTE: `globalThis` rather than `window`: the same in a browser, and tests
// can stub it.
export const safeLocalStorage = createSafeStorage(() => globalThis.localStorage)

export const safeSessionStorage = createSafeStorage(
  () => globalThis.sessionStorage,
)
